import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, MonitorCog } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import type { AdUnit } from '@/services/adUnits/adUnitsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import type { DiagnosticResult } from '@/services/diagnostics/diagnosticsApi'
import { DiagnosticParticipantTable } from './DiagnosticParticipantTable'

export interface DiagnosticResultCardProps {
  adUnit: AdUnit
  result?: DiagnosticResult
  /** Waiting for the ad units before it in the run. */
  queued: boolean
  loading: boolean
  error: unknown
  /** Line item names by id, filled in as they load. */
  lineItemNames: Map<string, string>
}

/** One ad unit's diagnostic result: a summary, then every line item in auction order. */
export function DiagnosticResultCard({
  adUnit,
  result,
  queued,
  loading,
  error,
  lineItemNames,
}: DiagnosticResultCardProps) {
  const { t } = useTranslation()
  const participants = result?.lineItemParticipants ?? []
  const winning = participants.filter((p) => p.reachedStage === 'WINNING')
  const heldBack = participants.filter((p) => p.reachedStage === 'ELIGIBLE').length

  return (
    <Card tone="insight">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CardIcon icon={MonitorCog} />
          <Link to={`/publishing/ad-units/${adUnit.id}`}>{adUnit.name}</Link>
        </CardTitle>
        <CardDescription>
          {[adUnit.site?.name, adUnit.tagId].filter(Boolean).join(' · ')}
        </CardDescription>
        {/* Where the ad server placed the request, from its IP or the lat & long given. */}
        {(result?.country || result?.city) && (
          <CardDescription className="text-xs">
            {t('diagnostics.location', {
              location: [result.city, result.country].filter(Boolean).join(', '),
            })}
            {result.temperature != null &&
              ` · ${t('diagnostics.temperature', { temperature: result.temperature })}`}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {queued && <p className="text-sm text-muted-foreground">{t('diagnostics.queued')}</p>}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('diagnostics.running')}
          </div>
        )}
        {!!error && (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertDescription>{apiErrorDescription(error)}</AlertDescription>
          </Alert>
        )}
        {result && (
          <>
            {winning.length > 0 && (
              <p className="text-sm">{t('diagnostics.winningBid', { cost: result.entryCost })}</p>
            )}
            {result.politicalApprovalRequired && (
              <Alert>
                <AlertTriangle />
                <AlertDescription>{t('diagnostics.politicalApprovalRequired')}</AlertDescription>
              </Alert>
            )}
            {((winning.length === 0 && participants.length > 0) || heldBack > 0) && (
              <p className="text-xs text-muted-foreground">
                {[
                  winning.length === 0 && participants.length > 0 && t('diagnostics.noWinner'),
                  heldBack > 0 && t('diagnostics.eligibleHint', { count: heldBack }),
                ]
                  .filter(Boolean)
                  .join(' ')}
              </p>
            )}
          </>
        )}
      </CardContent>
      {result && (
        <div className="border-t">
          <DiagnosticParticipantTable participants={participants} lineItemNames={lineItemNames} />
        </div>
      )}
    </Card>
  )
}
